import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import { SeatRequest } from "../../features/sessions/models/SeatRequest";
import { BookingMode } from "../../features/sessions/enums/BookingMode";
import { selectSessionSeats } from "../../features/sessions/seats-slice";
import { fetchSessionSeats } from "../../features/sessions/slice-extensions/seats-slice-extension";
import { formatSeatDate } from "../../features/sessions/utils/seatDateTime";
import SeatDecisionButtons from "./SeatDecisionButtons";

interface SessionSeatsCardProps {
  session: Session;
}

const groupByDate = (seats: SeatRequest[]): [string, SeatRequest[]][] => {
  const groups = new Map<string, SeatRequest[]>();
  for (const seat of seats) {
    groups.set(seat.occurrenceDate, [...(groups.get(seat.occurrenceDate) ?? []), seat]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
};

/**
 * The Session's booked dates for the next four weeks, grouped by date: Confirmed seats
 * with a "Booked" chip, Requested ones with Confirm / Decline. A Closed Session without
 * seats shows nothing.
 */
const SessionSeatsCard = ({ session }: SessionSeatsCardProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const seats = useAppSelector(selectSessionSeats(session.id));
  const groups = useMemo(() => groupByDate(seats), [seats]);

  useEffect(() => {
    dispatch(fetchSessionSeats({ bookingId: session.id }));
  }, [dispatch, session.id]);

  if (seats.length === 0 && (session.bookingMode ?? BookingMode.Closed) === BookingMode.Closed) {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-grey-20 dark:text-white">
        <CalendarCheck size={20} />
        {t("seats.cardTitle")}
      </h2>

      {groups.length === 0 ? (
        <p className="mt-3 text-sm text-grey-40 dark:text-grey-80">{t("seats.empty")}</p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {groups.map(([date, dateSeats]) => (
            <div key={date}>
              <p className="text-xs font-medium text-grey-40 dark:text-grey-80">
                {formatSeatDate(date, i18n.language)}
              </p>
              <ul className="mt-1 flex flex-col gap-2">
                {dateSeats.map((seat) => (
                  <li
                    key={seat.seatId}
                    className="flex items-center justify-between gap-3 rounded-xl bg-grey-95 px-3 py-2 dark:bg-primary-20"
                  >
                    <p className="min-w-0 truncate text-sm font-medium text-grey-20 dark:text-white">
                      {seat.memberName}
                    </p>
                    {seat.isPending ? (
                      <SeatDecisionButtons seat={seat} />
                    ) : (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        {t("seats.statusConfirmed")}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionSeatsCard;
